import { Action, Reducer } from 'redux';
import { AppThunkAction } from '.';
import axios, { AxiosError } from 'axios';
import { receiveAxiosError } from '@konicaminolta/common-react';
import { Group } from '../types/Group/Group';
import { REQUEST_GROUPS, RECEIVE_GROUPS, REQUEST_GROUP_DETAILS, RECEIVE_GROUP_DETAILS, UNLOAD_GROUP_DETAILS, GroupActionTypes } from '../types/actions';
import { CALL_HISTORY_METHOD } from 'connected-react-router';


// -----------------
// STATE - This defines the type of data maintained in the Redux store.

export interface GroupState {
    data: Group[];
    isLoadingData: boolean;
    isLoadedData: boolean;
    isLoadingDetails: boolean;
    isLoadedDetails: boolean;
    isLoadingRelationshipDetails: boolean;
    isLoadedRelationshipDetails: boolean;
    isUpdatingRelationshipDetails: boolean;
}

// ----------------
// ACTION CREATORS - These are functions exposed to UI components that will trigger a state transition.
// They don't directly mutate state, but they can have external side-effects (such as loading data).

export const actionCreators = {
    requestGroups: (): AppThunkAction<GroupActionTypes> => (dispatch, getState) => {

        // Only load data if it's something we don't already have (and are not already loading)
        const appState = getState();

        if (appState 
            && appState.group !== undefined 
            && !appState.group.isLoadingData 
            && !appState.group.isLoadedData) 
        {
            dispatch({ type: REQUEST_GROUPS });

            axios
                .request<Group[]>({
                    url: `./api/groups`
                })
                .then((response) => {
                    const { data } = response;
                    dispatch({ type: RECEIVE_GROUPS, data });
                })
                .catch((e: AxiosError) => {
                    dispatch(receiveAxiosError({error: e}));
                });
        }
    },

    requestGroupDetails: (groupId: string): AppThunkAction<GroupActionTypes> => (dispatch, getState) => {

        // Only load data if it's something we don't already have (and are not already loading)
        const appState = getState();

        if (appState 
            && appState.group !== undefined 
            && !appState.group.isLoadingDetails 
            && !appState.group.isLoadedDetails) 
        {
            let group = appState.group.data.find(x => x.id === groupId);

            if(group === undefined){
                dispatch({ type: REQUEST_GROUP_DETAILS });

                axios
                    .request<Group>({
                        url: `./api/groups/${groupId}`
                    })
                    .then((response) => {
                        const { data } = response;
                        dispatch({ type: RECEIVE_GROUP_DETAILS, group: data });
                    })
                    .catch((e: AxiosError) => {
                        dispatch(receiveAxiosError({error: e}));
                        dispatch({ type: RECEIVE_GROUP_DETAILS });

                        // if a specific resource is not found redirect to resource index page
                        if(e.response && e.response.status >= 400 && e.response.status < 500){
                            dispatch(
                                { 
                                    type: CALL_HISTORY_METHOD, 
                                    payload: { method: 'push', 'args': [ `/groups` ] }
                                }
                            );
                        }
                    });
            }
            else{
                dispatch({ type: RECEIVE_GROUP_DETAILS });
            }
        }
    },

    unloadTenantDetails: (): AppThunkAction<GroupActionTypes> => (dispatch) => {

        dispatch({ type: UNLOAD_GROUP_DETAILS });
    }
};

export const reducer: Reducer<GroupState> = (state: GroupState | undefined, incomingAction: Action): GroupState => {
    if (state === undefined) {
        return {
            data: [],
            isLoadingData: false,
            isLoadedData: false,
            isLoadingDetails: false,
            isLoadedDetails: false,
            isLoadingRelationshipDetails: false,
            isLoadedRelationshipDetails: false,
            isUpdatingRelationshipDetails: false,
        };
    }
const action = incomingAction as GroupActionTypes;

    switch (action.type) {
        case REQUEST_GROUPS: {
            return {
                ...state,
                isLoadingData: true,
                isLoadedData: false,
            };
        }
        case RECEIVE_GROUPS: {
            return {
                ...state,
                isLoadingData: false,
                isLoadedData: true,
                data: action.data,
            };
        }
        case REQUEST_GROUP_DETAILS: {
            return {
                ...state,
                isLoadingDetails: true,
                isLoadedDetails: false,
            };
        }
        case RECEIVE_GROUP_DETAILS: {
            // spread operator creates a shallow copy which will work for this specific transform of the state
            // since no nested properties are being affected
            let groups = [...state.data];
            if(action.group !== undefined){
                groups.push(action.group);
            }
            return {
                ...state,
                isLoadingDetails: false,
                isLoadedDetails: true,
                data: groups,
            };
        }
        case UNLOAD_GROUP_DETAILS: {
            return {
                ...state,
                isLoadingDetails: false,
                isLoadedDetails: false,
            };
        }
        /*
        case REQUEST_GROUP_RELATIONSHIP_DETAILS: {
            return {
                ...state,
                isLoadingRelationshipDetails: true,
                isLoadedRelationshipDetails: false,
            };
        }
        case RECEIVE_GROUP_RELATIONSHIP_DETAILS: {
            let tenants: Group[] = JSON.parse(JSON.stringify(state.data));
            let data = tenants;
            if(action.tenant !== undefined){
                data = tenants.filter(sub => sub.id !== action.tenant?.id);
                data.push(action.tenant)
            }
            return {
                ...state,
                isLoadingRelationshipDetails: false,
                isLoadedRelationshipDetails: true,
                data,
            };
        }
        case UNLOAD_GROUP_RELATIONSHIP_DETAILS: {
            return {
                ...state,
                isLoadingRelationshipDetails: false,
                isLoadedRelationshipDetails: false,
            };
        }
        case REQUEST_UPDATE_GROUP_RELATIONSHIP: {
            return {
                ...state,
                isUpdatingRelationshipDetails: true,
            };
        }
        case RECEIVE_UPDATE_GROUP_RELATIONSHIP: {
            return {
                ...state,
                isUpdatingRelationshipDetails: false,
                isLoadingRelationshipDetails: false,
                isLoadedRelationshipDetails: false,
            };
        }
        case RECEIVE_LINK_ACCOUNT_TENANT: {
            let tenants: Tenant[] = JSON.parse(JSON.stringify(state.data));
            let data = tenants.filter(sub => sub.id !== action.tenantId);
            return {
                ...state,
                isLoadingData: false,
                isLoadedData: false,
                isLoadingDetails: false,
                isLoadedDetails: false,
                data,
            };
        }
        case RECEIVE_UNLINK_ACCOUNT_TENANT: {
            let tenants: Tenant[] = JSON.parse(JSON.stringify(state.data));
            let data = tenants.filter(sub => sub.id !== action.tenantId);
            return {
                ...state,
                isLoadingData: false,
                isLoadedData: false,
                isLoadingDetails: false,
                isLoadedDetails: false,
                data
            };
        }
        */
    }

    return state;
};
